import axios, { AxiosError } from 'axios';

import config from '../../../envConfig';
import { Language } from '../../../shared/constants';
import { logAuth } from '../../../utils/auth/auth-debug-logger';
import { AUTH_ENDPOINTS } from './auth-endpoints';

const http = axios.create({ baseURL: config.apiBaseUrl });

export type SignInPushDevicePlatform = 'ios' | 'android' | 'web';

export interface ISignInPushDevice {
  fcmToken: string;
  platform: SignInPushDevicePlatform;
}

export interface ISignInPayload {
  uid: string;
  acceptCookiePolicy?: boolean;
  pushDevice?: ISignInPushDevice;
}

export interface ISignUpPayload {
  login: string;
  email?: string;
  uid: string;
  firstname?: string;
  lastname?: string;
  languageKey?: Language;
}

export const authApiService = {
  signIn(payload: ISignInPayload, accessToken: string) {
    logAuth('authApi:signIn:start', {
      uid: payload.uid,
      hasPushDevice: !!payload.pushDevice,
      tokenLength: accessToken.length,
    });
    return http
      .post(AUTH_ENDPOINTS.SIGNIN, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        logAuth('authApi:signIn:success', { status: res.status });
        return res;
      })
      .catch((error) => {
        logAuth('authApi:signIn:error', {
          status: getAuthApiStatus(error),
          errorCode: getAuthApiErrorCode(error),
        });
        throw error;
      });
  },

  signUp(payload: ISignUpPayload, accessToken: string) {
    return http
      .post(AUTH_ENDPOINTS.SIGNUP, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => res.data);
  },

  verify(email: string) {
    return http.post(AUTH_ENDPOINTS.VERIFY, { email }).then((res) => res.data);
  },

  checkEmailExists(email: string): Promise<boolean> {
    return http
      .post(AUTH_ENDPOINTS.CHECK_EMAIL, { email })
      .then((res) => res.data?.exists === true);
  },
};

export type AuthApiErrorBody = {
  code?: string;
  message?: string;
  statusCode?: number;
};

export function isAuthApiError(
  error: unknown,
): error is AxiosError<
  AuthApiErrorBody | { errors?: Record<string, string[]> }
> {
  return axios.isAxiosError(error);
}

export function getAuthApiStatus(error: unknown): number | undefined {
  return isAuthApiError(error) ? error.response?.status : undefined;
}

export function getAuthApiErrorCode(error: unknown): string | undefined {
  if (!isAuthApiError(error)) return undefined;
  const data = error.response?.data;
  return data && typeof data === 'object' && 'code' in data
    ? (data as AuthApiErrorBody).code
    : undefined;
}

export function isTransientAuthSignInError(error: unknown): boolean {
  if (error instanceof Error && error.message === 'No token') {
    return false;
  }
  const status = getAuthApiStatus(error);
  if (status !== undefined) {
    return status >= 500 && status <= 599;
  }
  return isAuthApiError(error) && !error.response;
}
